/**
 * D-3 (AUDITORIA-COMERCIAL) — carga que falha AVISA, uma vez.
 *
 * O achado: com o endpoint devolvendo 500, as 6 telas sondadas do Comercial
 * ficavam MUDAS — o kanban vazio de erro é indistinguível do kanban vazio de
 * verdade, e o vendedor conclui que não há trabalho. A causa era uma só:
 * `data ?? []` engole o `isError` de toda query, e nenhuma tela tinha canal
 * de erro.
 *
 * O conserto é por CAUSA: um `onError` no QueryCache GLOBAL (todas as queries
 * do app, presentes e futuras) que dispara o evento `carga:erro` — o mesmo
 * padrão do `auth:unauthorized` — e o `<AvisoDeCargaGlobal>` (montado sob o
 * ToastProvider) mostra UM toast pt-BR. Dedupe por janela: dez queries
 * falhando juntas (a tela típica) avisam UMA vez.
 *
 * 401 fica de fora: ele já tem desenho próprio (O10 — renova a sessão uma
 * vez e desloga se falhar); avisar "não carregou" a caminho do login seria
 * ruído.
 *
 * 🔴 KIT-INSUMO-ORCAMENTISTA (28/08/2026) — o evento passa a levar o STATUS.
 *
 * O aviso dizia "Confira a conexão e tente de novo" para QUALQUER falha, e o
 * orçamentista, que tomava **403** no bloco do kit insumo, tentava de novo —
 * e tentar de novo nunca ia funcionar. Mensagem que manda repetir o que não
 * pode dar certo é pior que mensagem nenhuma: gasta o tempo da pessoa e
 * esconde o pedido de acesso que resolveria.
 *
 * Quem decide o texto é o `<AvisoDeCargaGlobal>`; aqui só viaja o dado.
 */
import { QueryCache } from '@tanstack/react-query';

import { ApiError } from './api';

export const EVENTO_CARGA_ERRO = 'carga:erro';
const JANELA_DEDUPE_MS = 10_000;

/** O que o evento carrega: o status HTTP, quando houver um. */
export interface DetalheCargaErro {
  status: number | null;
}

/**
 * O texto de cada família de falha.
 *
 * 🔴 **403 não é erro de conexão.** Tentar de novo nunca resolve falta de
 * permissão — a mensagem tem de mandar pedir acesso. Erro de rede continua
 * falando de rede, e 5xx diz que quem falhou foi o sistema, não a internet
 * da pessoa.
 */
export function mensagemDeCarga(status: number | null): string {
  if (status === 403)
    return 'Você não tem acesso a esta parte da tela. Fale com o administrador para liberar.';
  if (status !== null && status >= 500)
    return 'O sistema respondeu com erro ao carregar os dados. Se continuar, avise o administrador.';
  return 'Não foi possível carregar os dados. Confira a conexão e tente de novo.';
}

let ultimoAvisoEm = 0;

/** Só pro teste conseguir recomeçar do zero — não usar em produção. */
export function zerarDedupeParaTeste(): void {
  ultimoAvisoEm = 0;
}

export function criarQueryCacheComAviso(): QueryCache {
  return new QueryCache({
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) return;
      const agora = Date.now();
      if (agora - ultimoAvisoEm < JANELA_DEDUPE_MS) return;
      ultimoAvisoEm = agora;
      const detail: DetalheCargaErro = {
        status: err instanceof ApiError ? err.status : null,
      };
      window.dispatchEvent(new CustomEvent<DetalheCargaErro>(EVENTO_CARGA_ERRO, { detail }));
    },
  });
}
